import { useEffect, useMemo, useState } from "react";

import { colors, radii, spacing } from "../../../../styles/design-system";
import { ActivityIndicator, StyleSheet, TouchableOpacity, View } from "react-native";
import { NotePencilIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";

import CustomModal from "../../../common/general/CustomModal/CustomModal";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";
import FormLayout from "../common/FormLayout";
import McqQuestionEditorModal from "./McqQuestionEditorModal";
import SocraticQuestionEditorModal from "./SocraticQuestionEditorModal";
import { useToast } from "../../../../contexts/Toast/ToastProvider";

import {
  AssessmentType,
  useDeleteMcqQuestionMutation,
  useDeleteSocraticQuestionMutation,
} from "../../../../graphql/generated/graphql";
import type { AiTutor, McqQuestion, Question } from "../../../../graphql/generated/graphql";

type EditorQuestion = McqQuestion | Question;
type SocraticQuestionEntry = {
  tutor: AiTutor;
  question: Question;
};

export type AssessmentQuestionBankModalProps = {
  visible: boolean;
  onClose: () => void;
  isLoading: boolean;
  errorMessage?: string | null;
  assessmentId?: string;
  assessmentType?: AssessmentType | null;
  aiTutors: AiTutor[];
  mcqQuestions?: McqQuestion[];
  onRefresh?: () => Promise<void> | void;
  pageSize?: number;
  socraticPage?: number;
  mcqPage?: number;
  socraticTotalCount?: number;
  mcqTotalCount?: number;
  onPageChange?: (params: { socraticPage?: number; mcqPage?: number }) => Promise<void> | void;
  draftMode?: boolean;
  submitText?: string;
  onSubmitDraft?: (payload: { aiTutors: AiTutor[]; mcqQuestions: McqQuestion[] }) => Promise<void> | void;
};

const optionLabel = (index: number) => String.fromCharCode(65 + index);
const AssessmentQuestionBankModal = ({
  visible,
  onClose,
  isLoading,
  errorMessage,
  assessmentId,
  assessmentType,
  aiTutors,
  mcqQuestions = [],
  onRefresh,
  pageSize = 25,
  socraticPage = 1,
  mcqPage = 1,
  socraticTotalCount,
  mcqTotalCount,
  onPageChange,
  draftMode = false,
  submitText = "Back",
  onSubmitDraft,
}: AssessmentQuestionBankModalProps) => {
  const { showToast } = useToast();
  const [deleteMcqQuestion, { loading: isDeletingQuestion }] = useDeleteMcqQuestionMutation();
  const [deleteSocraticQuestion, { loading: isDeletingSocraticQuestion }] = useDeleteSocraticQuestionMutation();
  const [editorMode, setEditorMode] = useState<"create" | "edit">("create");
  const [editorType, setEditorType] = useState<"mcq" | "socratic">("mcq");
  const [editingQuestion, setEditingQuestion] = useState<EditorQuestion | null>(null);
  const [editingSocraticQuestionAiTutorId, setEditingSocraticQuestionAiTutorId] = useState<string | null>(null);
  const [isQuestionEditorOpen, setIsQuestionEditorOpen] = useState(false);
  const [draftAiTutors, setDraftAiTutors] = useState<AiTutor[]>([]);
  const [draftMcqQuestions, setDraftMcqQuestions] = useState<McqQuestion[]>([]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    setDraftAiTutors(aiTutors);
    setDraftMcqQuestions(mcqQuestions);
  }, [visible, aiTutors, mcqQuestions]);

  const currentAiTutors = draftMode ? draftAiTutors : aiTutors;
  const currentMcqQuestions = draftMode ? draftMcqQuestions : mcqQuestions;
  const isMcqAssessment = assessmentType === AssessmentType.Mcq;
  const canManageQuestions = Boolean(assessmentId);
  const flattenedSocraticQuestions = useMemo<SocraticQuestionEntry[]>(() => {
    return currentAiTutors.flatMap((tutor) =>
      (tutor.questions ?? [])
        .filter((question) => question && question.isDeleted !== true)
        .filter(Boolean)
        .map((question) => ({ tutor, question }))
    );
  }, [currentAiTutors]);

  const totalQuestions = isMcqAssessment ? currentMcqQuestions.length : flattenedSocraticQuestions.length;
  const totalQuestionsInBank = draftMode
    ? totalQuestions
    : isMcqAssessment
      ? (mcqTotalCount ?? totalQuestions)
      : (socraticTotalCount ?? totalQuestions);
  const currentPage = isMcqAssessment ? mcqPage : socraticPage;
  const totalPages = Math.max(1, Math.ceil(Math.max(0, totalQuestionsInBank) / Math.max(1, pageSize)));
  const canLoadPreviousPage = currentPage > 1;
  const canLoadNextPage = currentPage < totalPages;
  const loadedLabel = draftMode ? `${totalQuestions}` : `${totalQuestions} loaded of ${totalQuestionsInBank}`;
  const totalIles = currentAiTutors.length;
  const questionLabel = `question${totalQuestions === 1 ? "" : "s"}`;
  const ileLabel = `ILE${totalIles === 1 ? "" : "s"}`;
  const questionBankSummaryText = `${loadedLabel} ${questionLabel} in bank from ${totalIles} ${ileLabel}`;

  const nextQuestionOrder = useMemo(() => {
    return currentMcqQuestions.length + 1;
  }, [currentMcqQuestions]);

  const closeQuestionEditor = () => setIsQuestionEditorOpen(false);

  const handleOpenQuestionEditor = (
    type: "mcq" | "socratic",
    mode: "create" | "edit",
    question?: EditorQuestion,
    selectedTutorId?: string | null
  ) => {
    setEditorMode(mode);
    setEditorType(type);
    setEditingQuestion(question ?? null);
    setEditingSocraticQuestionAiTutorId(selectedTutorId ?? (type === "mcq" ? null : (currentAiTutors[0]?.id ?? null)));
    setIsQuestionEditorOpen(true);
  };

  const handleOpenCreateQuestionModal = () => {
    handleOpenQuestionEditor(isMcqAssessment ? "mcq" : "socratic", "create");
  };

  const handleOpenEditQuestionModal = (question: McqQuestion) => {
    handleOpenQuestionEditor("mcq", "edit", question);
  };

  const handleOpenEditSocraticQuestionModal = (question: Question, tutor: AiTutor) => {
    handleOpenQuestionEditor("socratic", "edit", question, tutor.id ?? null);
  };

  const normalizeDraftQuestionId = (prefix: string) => {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  };

  const handleDraftMcqSave = (question: {
    id?: string | null;
    aiTutorId?: string | null;
    question: string;
    choices: string[];
    correctChoiceIndices: number[];
    explanation?: string | null;
    totalScore?: number | null;
    order?: number | null;
  }) => {
    const draftQuestionId = question.id ?? normalizeDraftQuestionId("draft-mcq");
    setDraftMcqQuestions((prev) => {
      const now = Date.now().toString();
      const existingDraftQuestion = prev.find((item) => item.id === question.id);
      const existingQuestionWithTutor = existingDraftQuestion as
        | (McqQuestion & { aiTutorId?: string | null; aiTutor?: { id?: string | null } | null })
        | undefined;
      const normalizedAiTutorId =
        question.aiTutorId ?? existingQuestionWithTutor?.aiTutorId ?? existingQuestionWithTutor?.aiTutor?.id ?? null;
      const draftQuestion = {
        __typename: "McqQuestion" as const,
        id: draftQuestionId,
        question: question.question,
        choices: question.choices,
        correctChoiceIndices: question.correctChoiceIndices,
        explanation: question.explanation ?? null,
        totalScore: question.totalScore ?? 1,
        order: question.order ?? existingDraftQuestion?.order ?? prev.length + 1,
        isDeleted: false,
        created: now,
        lastModified: now,
        ...(normalizedAiTutorId ? { aiTutorId: normalizedAiTutorId } : {}),
      } as unknown as McqQuestion;

      if (question.id) {
        const nextQuestions = prev.map((existingQuestion) =>
          existingQuestion.id === question.id ? draftQuestion : existingQuestion
        );
        if (nextQuestions.some((item) => item.id === question.id)) {
          return nextQuestions;
        }
      }

      return [...prev, draftQuestion];
    });
  };

  const handleDraftSocraticSave = (question: {
    id?: string | null;
    aiTutorId: string;
    question: string;
    totalScore?: number | null;
    rubrics: Array<{ component: string; points: number }>;
  }) => {
    const draftQuestion = {
      __typename: "Question" as const,
      id: question.id ?? normalizeDraftQuestionId(`draft-socratic-${question.aiTutorId}`),
      question: question.question,
      totalScore: question.totalScore ?? null,
      isDeleted: false,
      rubrics: question.rubrics.map((rubric, rubricIndex) => ({
        __typename: "Rubric" as const,
        id: normalizeDraftQuestionId(`draft-rubric-${question.aiTutorId}-${rubricIndex}`),
        component: rubric.component,
        points: rubric.points,
      })),
    } as unknown as Question;

    setDraftAiTutors((prev) =>
      prev.map((tutor) => {
        if (tutor.id !== question.aiTutorId) {
          return tutor;
        }

        const draftQuestions = tutor.questions ?? [];
        if (!question.id) {
          return {
            ...tutor,
            questions: [...draftQuestions, draftQuestion],
          };
        }

        const replacedQuestions = draftQuestions.map((existingQuestion) =>
          existingQuestion.id === question.id ? draftQuestion : existingQuestion
        );
        if (replacedQuestions.some((existingQuestion) => existingQuestion.id === question.id)) {
          return {
            ...tutor,
            questions: replacedQuestions,
          };
        }

        return {
          ...tutor,
          questions: [...draftQuestions, draftQuestion],
        };
      })
    );
  };

  const handleDeleteQuestion = async (questionType: "mcq" | "socratic", questionId: string) => {
    if (!questionId) {
      return;
    }

    if (draftMode) {
      if (questionType === "mcq") {
        setDraftMcqQuestions((prev) => prev.filter((question) => question.id !== questionId));
      } else {
        setDraftAiTutors((prev) =>
          prev.map((tutor) => ({
            ...tutor,
            questions: (tutor.questions ?? []).filter((question) => question?.id !== questionId),
          }))
        );
      }
      return;
    }

    try {
      if (questionType === "mcq") {
        const { data } = await deleteMcqQuestion({
          variables: {
            mcqQuestionId: questionId,
            assessmentId,
          },
        });

        const response = data?.deleteMcqQuestion;

        if (!response?.success) {
          showToast({
            message: response?.message || "Failed to delete question.",
            type: "error",
          });
          return;
        }
      } else {
        const { data } = await deleteSocraticQuestion({
          variables: {
            questionId,
            assessmentId,
          },
        });

        const response = data?.deleteSocraticQuestion;

        if (!response?.success) {
          showToast({
            message: response?.message || "Failed to delete question.",
            type: "error",
          });
          return;
        }
      }

      await onRefresh?.();
      showToast({
        message: "Question deleted successfully.",
        type: "success",
      });
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : "Failed to delete question.",
        type: "error",
      });
    }
  };

  const isDeletingAnyQuestion = isDeletingQuestion || isDeletingSocraticQuestion;
  const socraticQuestionForEditor = editorType === "socratic" ? (editingQuestion as Question | null) : null;
  const canShowCreateQuestionButton = canManageQuestions || draftMode;
  const handleDraftSubmit = async () => {
    if (!draftMode) {
      onClose();
      return;
    }

    await onSubmitDraft?.({ aiTutors: draftAiTutors, mcqQuestions: draftMcqQuestions });
  };

  const handlePreviousPage = async () => {
    if (draftMode || !onPageChange || !canLoadPreviousPage) {
      return;
    }

    if (isMcqAssessment) {
      await onPageChange({ mcqPage: currentPage - 1 });
      return;
    }

    await onPageChange({ socraticPage: currentPage - 1 });
  };

  const handleNextPage = async () => {
    if (draftMode || !onPageChange || !canLoadNextPage) {
      return;
    }

    if (isMcqAssessment) {
      await onPageChange({ mcqPage: currentPage + 1 });
      return;
    }

    await onPageChange({ socraticPage: currentPage + 1 });
  };

  return (
    <CustomModal visible={visible} onRequestClose={onClose} ariaLabel="Assessment Question Bank">
      <FormLayout
        title="Question Bank"
        isSubmitting={false}
        onCancel={onClose}
        onSubmit={handleDraftSubmit}
        actionText={submitText}
        hideCancelButton={!draftMode}
        buttonsContainerStyle={{ justifyContent: draftMode ? "space-between" : "flex-end" }}
        contentContainerStyle={{ paddingBottom: 0 }}>
        {isLoading ? (
          <View style={styles.loading}>
            <ActivityIndicator size="large" color={colors.brand[500]} />
          </View>
        ) : (
          <>
            {!!errorMessage && (
              <>
                <CustomText text={errorMessage} useTranslationText={false} style={styles.errorText} />
                <CustomSpacing size="m" />
              </>
            )}

            {canShowCreateQuestionButton && (
              <>
                <View style={styles.actionsRow}>
                  <CustomButton
                    text="Add Question"
                    useTranslationText={false}
                    styleType="primaryTransparentStroke"
                    size="s"
                    leftIcon={<PlusIcon size={16} color={colors.brand[500]} />}
                    onPress={handleOpenCreateQuestionModal}
                  />
                </View>
                <CustomSpacing size="m" />
              </>
            )}

            <View style={styles.summaryPill}>
              <CustomText
                text={questionBankSummaryText}
                size="s"
                useTranslationText={false}
                style={styles.summaryText}
                weight="normal"
              />
            </View>
            {!draftMode && (
              <>
                <CustomSpacing size="s" />
                <View style={styles.paginationRow}>
                  <CustomButton
                    text="Prev"
                    useTranslationText={false}
                    size="s"
                    styleType="primaryTransparentStroke"
                    onPress={() => {
                      void handlePreviousPage();
                    }}
                    disabled={isLoading || !canLoadPreviousPage}
                  />
                  <CustomText
                    text={`Page ${currentPage} of ${totalPages}`}
                    size="s"
                    useTranslationText={false}
                    style={styles.paginationText}
                    weight="normal"
                  />
                  <CustomButton
                    text="Next"
                    useTranslationText={false}
                    size="s"
                    styleType="primaryTransparentStroke"
                    onPress={() => {
                      void handleNextPage();
                    }}
                    disabled={isLoading || !canLoadNextPage}
                  />
                </View>
              </>
            )}

            {isMcqAssessment ? (
              currentMcqQuestions.length === 0 ? (
                <CustomText
                  text="No MCQ questions found for this assessment."
                  useTranslationText={false}
                  style={styles.emptyText}
                />
              ) : (
                <>
                  {currentMcqQuestions.map((question, idx) => {
                    return (
                      <View key={question.id ?? `mcq-${idx}`} style={styles.questionCard}>
                        <View style={styles.questionCardHeader}>
                          <View style={styles.questionHeaderTopRow}>
                            <CustomText
                              text={`QUESTION ${idx + 1}`}
                              size="xs"
                              useTranslationText={false}
                              style={styles.questionIndex}
                              weight="normal"
                            />
                            {canManageQuestions && (
                              <View style={styles.questionActions}>
                                <TouchableOpacity
                                  style={styles.questionActionButton}
                                  onPress={() => handleOpenEditQuestionModal(question)}
                                  disabled={isDeletingAnyQuestion}
                                  accessibilityRole="button"
                                  aria-label={`Edit question ${idx + 1}`}>
                                  <NotePencilIcon size={16} color={colors.grey[500]} />
                                </TouchableOpacity>
                                <TouchableOpacity
                                  style={styles.questionActionButton}
                                  onPress={() => {
                                    if (question.id) {
                                      void handleDeleteQuestion("mcq", question.id);
                                    }
                                  }}
                                  disabled={isDeletingAnyQuestion}
                                  accessibilityRole="button"
                                  aria-label={`Delete question ${idx + 1}`}>
                                  <TrashIcon size={16} color={colors.grey[500]} />
                                </TouchableOpacity>
                              </View>
                            )}
                          </View>
                          <CustomText
                            text={question.question ?? ""}
                            size="m"
                            useTranslationText={false}
                            style={styles.questionTitle}
                            weight="normal"
                          />
                        </View>

                        <View style={styles.questionCardBody}>
                          <CustomText
                            text="OPTIONS"
                            size="xs"
                            useTranslationText={false}
                            style={styles.sectionLabel}
                            weight="normal"
                          />
                          <CustomSpacing size="s" />

                          <View style={styles.rubricsWrap}>
                            {(question.choices ?? []).map((choice, choiceIndex) => {
                              const correctIndices =
                                (question as { correctChoiceIndices?: number[] }).correctChoiceIndices ?? [];
                              const isCorrect = correctIndices.includes(choiceIndex);

                              return (
                                <View
                                  key={`${question.id ?? idx}-${choiceIndex}`}
                                  style={[styles.choiceRow, isCorrect && styles.correctChoiceRow]}>
                                  <CustomText
                                    text={`${optionLabel(choiceIndex)}.`}
                                    size="s"
                                    useTranslationText={false}
                                    style={[styles.choiceIndex, isCorrect && styles.correctChoiceIndex]}
                                    weight="normal"
                                  />
                                  <CustomText
                                    text={choice}
                                    size="s"
                                    useTranslationText={false}
                                    style={styles.choiceText}
                                    weight="normal"
                                  />
                                </View>
                              );
                            })}
                          </View>

                          {!!question.explanation && (
                            <>
                              <CustomSpacing size="m" />
                              <CustomText
                                text="EXPLANATION"
                                size="xs"
                                useTranslationText={false}
                                style={styles.sectionLabel}
                                weight="normal"
                              />
                              <CustomSpacing size="xs" />
                              <CustomText
                                text={question.explanation}
                                size="s"
                                useTranslationText={false}
                                style={styles.explanationText}
                                weight="normal"
                              />
                            </>
                          )}

                          <CustomSpacing size="m" />
                          <View style={styles.totalRow}>
                            <CustomText
                              text={`Total score: ${question.totalScore ?? 0}`}
                              size="s"
                              useTranslationText={false}
                              style={styles.totalText}
                              weight="normal"
                            />
                          </View>
                        </View>

                        <View style={styles.questionCardFooter}>
                          <CustomText
                            text="Source: Assessment Question Bank"
                            size="s"
                            useTranslationText={false}
                            style={styles.footerText}
                            weight="normal"
                          />
                        </View>
                      </View>
                    );
                  })}
                </>
              )
            ) : flattenedSocraticQuestions.length === 0 ? (
              <CustomText
                text="No socratic questions found for this assessment."
                useTranslationText={false}
                style={styles.emptyText}
              />
            ) : (
              flattenedSocraticQuestions.map(({ tutor, question }, idx) => {
                const socraticQuestion = question;
                const rubrics = socraticQuestion.rubrics ?? [];
                const totalPoints =
                  typeof socraticQuestion.totalScore === "number"
                    ? socraticQuestion.totalScore
                    : rubrics.reduce((s, r) => s + (typeof r.points === "number" ? r.points : 0), 0);

                return (
                  <View key={question.id ?? `${tutor.id}-${idx}`} style={styles.questionCard}>
                    <View style={styles.questionCardHeader}>
                      <View style={styles.questionHeaderTopRow}>
                        <CustomText
                          text={`QUESTION ${idx + 1}`}
                          size="xs"
                          useTranslationText={false}
                          style={styles.questionIndex}
                          weight="normal"
                        />
                        {canManageQuestions && (
                          <View style={styles.questionActions}>
                            <TouchableOpacity
                              style={styles.questionActionButton}
                              onPress={() => handleOpenEditSocraticQuestionModal(socraticQuestion, tutor)}
                              disabled={isDeletingAnyQuestion}
                              accessibilityRole="button"
                              aria-label={`Edit question ${idx + 1}`}>
                              <NotePencilIcon size={16} color={colors.grey[500]} />
                            </TouchableOpacity>
                            <TouchableOpacity
                              style={styles.questionActionButton}
                              onPress={() => {
                                if (question.id) {
                                  void handleDeleteQuestion("socratic", question.id);
                                }
                              }}
                              disabled={isDeletingAnyQuestion}
                              accessibilityRole="button"
                              aria-label={`Delete question ${idx + 1}`}>
                              <TrashIcon size={16} color={colors.grey[500]} />
                            </TouchableOpacity>
                          </View>
                        )}
                      </View>
                      <CustomText
                        text={socraticQuestion.question ?? ""}
                        size="m"
                        useTranslationText={false}
                        style={styles.questionTitle}
                        weight="normal"
                      />
                    </View>

                    <View style={styles.questionCardBody}>
                      <CustomText
                        text="RUBRIC CRITERIA"
                        size="xs"
                        useTranslationText={false}
                        style={styles.sectionLabel}
                        weight="normal"
                      />
                      <CustomSpacing size="s" />

                      {rubrics.length === 0 ? (
                        <CustomText
                          text="No rubrics available for this question."
                          useTranslationText={false}
                          style={styles.emptyText}
                        />
                      ) : (
                        <View style={styles.rubricsWrap}>
                          {rubrics.map((r) => (
                            <View key={r.id} style={styles.rubricRow}>
                              <CustomText
                                text={r.component ?? ""}
                                size="s"
                                useTranslationText={false}
                                style={styles.rubricText}
                                weight="normal"
                              />
                              <CustomText
                                text={`${r.points ?? 0} pts`}
                                size="s"
                                useTranslationText={false}
                                style={styles.rubricPoints}
                                weight="normal"
                              />
                            </View>
                          ))}
                        </View>
                      )}

                      <CustomSpacing size="m" />
                      <View style={styles.totalRow}>
                        <CustomText
                          text={`Total: ${totalPoints} pts`}
                          size="s"
                          useTranslationText={false}
                          style={styles.totalText}
                          weight="normal"
                        />
                      </View>
                    </View>

                    <View style={styles.questionCardFooter}>
                      <CustomText
                        text={`Source: ${tutor.title ?? "Interactive Learning Experience"}`}
                        size="s"
                        useTranslationText={false}
                        style={styles.footerText}
                        weight="normal"
                      />
                    </View>
                  </View>
                );
              })
            )}
          </>
        )}
      </FormLayout>

      <McqQuestionEditorModal
        visible={isQuestionEditorOpen && editorType === "mcq"}
        mode={editorMode}
        assessmentId={assessmentId}
        question={editorType === "mcq" ? (editingQuestion as McqQuestion | null) : null}
        nextOrder={nextQuestionOrder}
        draftMode={draftMode}
        onDraftSave={handleDraftMcqSave}
        onClose={closeQuestionEditor}
        onSaved={async () => {
          await onRefresh?.();
        }}
      />
      <SocraticQuestionEditorModal
        visible={isQuestionEditorOpen && editorType === "socratic"}
        mode={editorMode}
        aiTutors={currentAiTutors}
        assessmentId={assessmentId}
        selectedAiTutorId={editingSocraticQuestionAiTutorId}
        question={socraticQuestionForEditor}
        draftMode={draftMode}
        onDraftSave={handleDraftSocraticSave}
        onClose={closeQuestionEditor}
        onSaved={async () => {
          await onRefresh?.();
        }}
      />
    </CustomModal>
  );
};

const styles = StyleSheet.create({
  errorText: {
    color: colors.error[600],
  },
  emptyText: {
    color: colors.grey[500],
  },
  loading: {
    paddingVertical: 32,
    alignItems: "center",
    justifyContent: "center",
  },
  summaryPill: {
    backgroundColor: colors.grey[50],
    borderRadius: radii.lg,
    paddingHorizontal: 14,
    paddingVertical: spacing.sm,
    borderWidth: 1,
    borderColor: colors.grey[100],
    marginBottom: 14,
  },
  summaryText: {
    color: colors.grey[700],
  },
  paginationRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  paginationText: {
    color: colors.grey[700],
    flex: 1,
    textAlign: "center",
  },
  questionCard: {
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: radii.lg,
    overflow: "hidden",
    marginBottom: 14,
    backgroundColor: colors.white,
  },
  questionCardHeader: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 14,
    backgroundColor: colors.grey[50],
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  questionHeaderTopRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  questionActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  questionActionButton: {
    width: 28,
    height: 28,
    borderRadius: radii.md,
    alignItems: "center",
    justifyContent: "center",
  },
  questionIndex: {
    color: colors.grey[500],
    letterSpacing: 0.6,
  },
  actionsRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
  },
  questionTitle: {
    color: colors.grey[900],
    marginTop: 6,
    lineHeight: 22,
    fontSize: 16,
  },
  questionCardBody: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 14,
  },
  rubricsWrap: {
    gap: spacing.xs,
  },
  sectionLabel: {
    color: colors.grey[500],
    letterSpacing: 0.6,
  },
  rubricRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.sm,
    backgroundColor: colors.brand[50],
    borderRadius: radii.lg,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
  },
  rubricText: {
    color: colors.grey[900],
    flex: 1,
  },
  rubricPoints: {
    color: colors.brand[500],
  },
  choiceRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
    backgroundColor: colors.grey[50],
    borderRadius: radii.lg,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderWidth: 1,
    borderColor: colors.grey[200],
  },
  correctChoiceRow: {
    backgroundColor: colors.success[50],
    borderColor: colors.success[200],
  },
  choiceIndex: {
    color: colors.grey[500],
  },
  correctChoiceIndex: {
    color: colors.success[600],
  },
  choiceText: {
    color: colors.grey[900],
    flex: 1,
  },
  explanationText: {
    color: colors.grey[700],
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
  },
  totalText: {
    color: colors.grey[700],
  },
  questionCardFooter: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    backgroundColor: colors.grey[50],
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
  },
  footerText: {
    color: colors.grey[700],
  },
});

export default AssessmentQuestionBankModal;
